import { Component, HostListener, OnDestroy } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { debounceTime, distinctUntilChanged, map, Subject, takeUntil } from 'rxjs';
import { PATHS } from 'src-ironside/app-routing.module';
import { Filter } from 'src-ironside/repositories/common.repository';
import { Partner, PartnerRepository } from 'src-ironside/repositories/partner.repository';
import { DynamicProductParam } from 'src-ironside/repositories/product.repository';
import { ResourceRepository } from 'src-ironside/repositories/resource.repository';
import { SupplierRepository } from 'src-ironside/repositories/supplier.repository';
import { AccueilDataService } from 'src-ironside/services/accueil-data.service';
import { PermissionService } from 'src-ironside/services/permission.service';
import { ScreenMediator } from 'src-ironside/services/screen.mediator';
import { BreadCrumbItem } from 'src-ironside/shared/components/bread-crumb/bread-crumb.component';
import { DynamicViewInfo } from 'src/app/shared/entities/dynamic-view';
import { DynamicDocumentService } from 'src/app/shared/services/dynamic-document.service';

interface PageEvent {
    first: number;
    rows: number;
    page: number;
    pageCount: number;
}

@Component({
    selector: 'app-list-partners',
    templateUrl: 'list-partners.component.html',
    styleUrls: ['./list-partners.component.css'],
    providers: [PartnerRepository, ResourceRepository, SupplierRepository],
})
export class ListPartnersComponent implements OnDestroy {
    partnerName: string = '';
    listPartners: Partner[];
    formPagination!: FormGroup;
    totalRecords: number = 120;
    listRows = [10, 20, 30];
    first: number = 0;

    pageSize: number = 30;

    exporterStautus = false;
    exportViews?: DynamicViewInfo[];

    destroyRef = new Subject();
    loadingExport: boolean = false;
    autocompleteSearchlistParner: any[] = [];
    searchTerm$ = new Subject<string>();
    loading: boolean;

    @HostListener('document:click', ['$event']) onDocumentClick(event: any): void {
        if (this.exporterStautus) {
            if (
                event.target.className !== 'btn-common' &&
                event.target.className !== 'exporter-item d-flex align-items-center'
            ) {
                this.exporterStautus = false;
            }
        }
    }

    get breadCrumb() {
        const breadCrumbs: BreadCrumbItem[] = [
            { name: this.translate.instant('Menu.home'), url: PATHS.home_en },
            { name: this.translate.instant('Menu.the-partners') },
        ];
        return breadCrumbs;
    }

    constructor(
        private partnerRepository: PartnerRepository,
        private screenMediator: ScreenMediator,
        private dynamicDocumentService: DynamicDocumentService,
        private supplierRepo: SupplierRepository,
        private translate: TranslateService,
        private accueilDataService: AccueilDataService,
        public permissionService: PermissionService
    ) {}

    ngOnInit() {
        this.setSearchPipe();
        this.getListPartners();
    }

    ngOnDestroy(): void {
        this.destroyRef.next(null);
        this.destroyRef.complete();
    }

    emptyList() {
        setTimeout(() => {
            this.autocompleteSearchlistParner = [];
        }, 300);
    }

    onKeyUp(event: KeyboardEvent) {
        if (event.key === 'Enter') {
            this.getListPartners();
        }
    }

    onSearchInput(): void {
        this.searchTerm$.next(this.partnerName);
    }

    selectItem(item: any) {
        if (item.id) {
            this.accueilDataService.filterData = {};
            const encodedTitle = encodeURIComponent(item.title);
            this.screenMediator.toPartnerDetail(item.code, encodedTitle);
        }
    }

    setSearchPipe() {
        this.searchTerm$
            .pipe(debounceTime(500), distinctUntilChanged())
            .pipe(takeUntil(this.destroyRef))
            .subscribe((searchTerm: string) => {
                if (searchTerm) {
                    this.autocomplete(searchTerm);
                } else {
                    this.emptyList();
                }
            });
    }

    autocomplete(term: string) {
        if (term.length > 0) {
            const filter: Filter[] = [
                ...this.partnerRepository.defaultFilters,
                {
                    fieldName: 'companyName',
                    conditions: [
                        {
                            type: 'CONTAINS',
                            value: this.partnerName,
                        },
                    ],
                },
            ];
            const body: DynamicProductParam = {
                collectionName: 'dynamic_legal_entity',
                filters: filter,
                page: 1,
                pageSize: this.pageSize,
                fields: ['companyName', 'code'],
                sorts: { companyName: 'ASC' },
            };
            this.loading = true;
            this.dynamicDocumentService
                .getListDynamicDocumentByCollectionName(body)
                .pipe(
                    map((res) => {
                        return {
                            content: res.content.map((parner: any) => ({
                                id: parner?.id,
                                title: parner?.companyName,
                                phone: parner?.phone,
                                fax: parner?.fax,
                                email: parner?.email,
                                city: parner?.city,
                                code: parner?.code,
                                resources: parner.linkToResources?.map(
                                    (resource: any) => resource.documentId
                                ),
                                logo: parner?.logo?.[0]?.url,
                            })),
                            totalElements: res.totalElements,
                        };
                    })
                )
                .pipe(takeUntil(this.destroyRef))
                .subscribe({
                    next: (res) => {
                        if (this.partnerName?.length > 0) {
                            this.autocompleteSearchlistParner = res?.content;
                        } else {
                            this.emptyList();
                        }
                        this.loading = false;
                    },
                    error: (error) => {
                        this.loading = false;
                    },
                });
        } else {
            this.emptyList();
        }
    }

    getListPartners(page: number = 1) {
        const filter: Filter[] = [
            ...this.partnerRepository.defaultFilters,
            {
                fieldName: 'companyName',
                conditions: [
                    {
                        type: 'CONTAINS',
                        value: this.partnerName,
                    },
                ],
            },
        ];

        let body: DynamicProductParam = {
            collectionName: 'dynamic_legal_entity',
            filters: filter,
            fields: [
                'id',
                'companyName',
                'phone',
                'fax',
                'email',
                'city',
                'code',
                'linkToResources',
                'logo',
            ],
            page: page,
            pageSize: this.pageSize,
            sorts: { companyName: 'IGNORECASE ASC' },
        };
        this.dynamicDocumentService
            .getListDynamicDocumentByCollectionName(body)
            .pipe(
                map((res) => ({
                    content: res.content.map((partner) => ({
                        id: partner.id,
                        title: partner.companyName,
                        phone: partner.phone,
                        fax: partner.fax,
                        email: partner.email,
                        city: partner.city,
                        code: partner.code,
                        resources: partner.linkToResources?.map(
                            (resource: any) => resource.documentId
                        ),
                        logo: partner?.logo?.[0]?.url,
                    })),
                    totalElements: res.totalElements,
                }))
            )
            .pipe(takeUntil(this.destroyRef))
            .subscribe({
                next: (res) => {
                    this.listPartners = res.content;
                    this.totalRecords = res.totalElements;
                },
                error: (error) => {},
            });
        window.scrollTo(0, 0);
    }

    onPageChange3(event: PageEvent) {
        this.first = event.first;
        this.getListPartners(event.page + 1);
    }

    goDetail(partner: Partner | undefined) {
        if (!partner?.code || !partner.title) {
            return;
        }
        this.screenMediator.toPartnerDetail(partner.code, partner.title);
    }

    dropdownExporter(): void {
        if (!this.exportViews) {
            this.partnerRepository
                .getViews()
                .pipe(takeUntil(this.destroyRef))
                .subscribe((res) => {
                    this.exportViews = res;
                });
        }
        this.exporterStautus = !this.exporterStautus;
    }

    export(viewId: string) {
        this.partnerRepository.export(viewId);
    }

    onExporterPartenalres() {
        this.loadingExport = true;
        this.supplierRepo
            .exportSupplierExtra('6788f4358e8f0845c3a7a8e8', 'PARTENAIRE')
            .pipe(takeUntil(this.destroyRef))
            .subscribe({
                next: (data: Blob) => {
                    const nameExport = 'export_partners.xlsx';
                    const blob = new Blob([data], { type: 'application/vnd.ms-excel' });
                    const objectUrl = window.URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    link.href = objectUrl;
                    link.download = nameExport;
                    link.click();
                    URL.revokeObjectURL(objectUrl);
                    this.loadingExport = false;
                },
                error: (err) => {
                    this.loadingExport = false;
                },
            });
    }
}
